/**
 * @fileoverview Tech Stack Analysis Dashboard
 * Visualizes an agency's technology stack derived from procurement data.
 * @module TechStackAnalysis
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Calendar, Hash, Layers, RefreshCw, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Hooks
import useTechStack from './hooks/useTechStack';
import useHapticFeedback from './hooks/useHapticFeedback';
import { useTimeFilterContext } from '../../contexts/TimeFilterContext';

// Components
import SummaryStats from './components/SummaryStats';
import AlertStrip from './components/AlertStrip';
import ViewSelector from './components/ViewSelector';
import DomainGrid from './components/DomainGrid';
import SharedTransactionsDrilldownModal from '../shared/SharedTransactionsDrilldownModal';
import RenewalsTimeline from './components/RenewalsTimeline';
import { getDateRangeStrings } from '../UnifiedTimeFilter';
import apiClient from '../../lib/apiClient';

// Utilities
import logger from '../../lib/logger';

// Styles
import './TechStackAnalysis.css';

/**
 * Loading skeleton component
 */
const LoadingSkeleton = () => (
  <div className="ts-dashboard">
    <div className="ts-header">
      <div className="ts-skeleton" style={{ width: 200, height: 32 }} />
    </div>
    <div className="ts-summary-stats">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="ts-skeleton ts-skeleton-stat" />
      ))}
    </div>
    <div className="ts-skeleton" style={{ width: '100%', height: 48, marginBottom: 16 }} />
    <div className="ts-domain-grid">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="ts-skeleton ts-skeleton-card" />
      ))}
    </div>
  </div>
);

/**
 * Error display component
 */
const ErrorDisplay = ({ error, onRetry }) => (
  <div className="ts-error">
    <AlertCircle size={48} />
    <span>Error loading tech stack data: {error}</span>
    <button className="ts-btn ts-btn-primary" onClick={onRetry}>
      Retry
    </button>
  </div>
);

/**
 * Risk score badge component
 */
const RiskBadge = ({ score }) => {
  const gradeClass = `grade-${score?.toLowerCase() || 'a'}`;

  return (
    <div className="ts-risk-badge">
      <span className={`ts-risk-letter ${gradeClass}`}>{score || 'A'}</span>
      <div className="ts-risk-detail">
        <span className="ts-risk-label">Risk Score</span>
        <span className="ts-risk-sub">
          {score === 'A' ? 'Low risk' :
           score === 'B' ? 'Moderate' : 'Needs attention'}
        </span>
      </div>
    </div>
  );
};

/**
 * Main Tech Stack Analysis Dashboard Component
 * @param {Object} props
 * @param {Object} props.selectedDepartment - Selected department object
 */
const TechStackAnalysis = ({ selectedDepartment }) => {
  const navigate = useNavigate();
  const haptic = useHapticFeedback();

  // Global time filter from context - selection persists across navigation
  const { legacyPeriod: activePeriod } = useTimeFilterContext();

  // State
  const [activeView, setActiveView] = useState('full-stack');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [drillModalOpen, setDrillModalOpen] = useState(false);

  // Extract department ID
  const departmentId = selectedDepartment?.department_id ||
                       selectedDepartment?.official_name ||
                       selectedDepartment;

  // Fetch data - uses global time filter from context
  const { data, loading, error, refetch } = useTechStack({
    departmentId,
    view: activeView,
    period: activePeriod
  });

  // Log component mount
  useEffect(() => {
    logger.component.info('TechStack', 'mounted', { departmentId });

    return () => {
      logger.component.info('TechStack', 'unmounted');
    };
  }, [departmentId]);

  // Log view changes
  useEffect(() => {
    logger.ui.info('TechStack', 'view_changed', { view: activeView });
  }, [activeView]);

  /**
   * Handle view tab change
   */
  const handleViewChange = useCallback((viewId) => {
    setActiveView(viewId);
    setSelectedVendor(null);
    setDrillModalOpen(false);
    haptic.select();
    logger.ui.info('TechStack', 'view_changed', { view: viewId });
  }, [haptic]);

  // Note: Time period is now controlled by the global filter in the app shell (TopNav).
  // No local handlePeriodChange needed - selection persists across navigation.

  /**
   * Handle vendor pill click
   */
  const handleVendorClick = useCallback((vendor) => {
    setSelectedVendor(vendor);
    setDrillModalOpen(true);
    haptic.medium();
    logger.ui.info('TechStack', 'vendor_clicked', {
      componentId: vendor.vendorId,
      vendorName: vendor.vendorName,
      productName: vendor.productName,
    });
  }, [haptic]);

  /**
   * Handle drill modal close
   */
  const handleDrillModalClose = useCallback(() => {
    setDrillModalOpen(false);
    setSelectedVendor(null);
    haptic.light();
  }, [haptic]);

  /**
   * Handle alert action click
   */
  const handleAlertAction = useCallback((url) => {
    haptic.light();
    navigate(url);
  }, [navigate, haptic]);

  /**
   * Handle refresh button click
   */
  const handleRefresh = useCallback(() => {
    haptic.light();
    refetch();
  }, [haptic, refetch]);

  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error && !data) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  return (
    <div className="ts-dashboard">
      {/* Header */}
      <div className="ts-header">
        <div className="ts-header-top">
          <div className="ts-header-left">
            <Layers size={28} />
            <div>
              <h1>Tech Stack Analysis</h1>
              <p>Procurement technology overview</p>
            </div>
          </div>
          <div className="ts-header-actions">
            {/* Time filter is now in the global app shell (TopNav) - persists across navigation */}
            <RiskBadge score={data?.summary?.riskScore} />
            <button
              className="ts-btn ts-btn-ghost"
              onClick={handleRefresh}
              title="Refresh data"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <SummaryStats summary={data?.summary} period={activePeriod} />

      {/* Alerts */}
      <AlertStrip
        alerts={data?.alerts}
        onActionClick={handleAlertAction}
      />

      {/* View Selector Tabs */}
      <ViewSelector
        activeView={activeView}
        onViewChange={handleViewChange}
        onHaptic={haptic.select}
      />

      {/* Domain Grid */}
      <DomainGrid
        domains={data?.domains}
        selectedVendor={selectedVendor}
        onVendorClick={handleVendorClick}
        onHaptic={haptic.light}
      />

      {/* Renewals Timeline */}
      <RenewalsTimeline renewals={data?.renewals} />

      {/* Product Transactions Modal — shared drilldown, scoped by component_id */}
      {drillModalOpen && selectedVendor && (
        <TechStackDrilldown
          selectedVendor={selectedVendor}
          departmentId={departmentId}
          period={activePeriod}
          onClose={handleDrillModalClose}
        />
      )}
    </div>
  );
};

// Tech Stack drilldown wrapper — adapts the page state (selected vendor +
// component_id, department, time period) into the shared modal. Uses the
// SharedTransactionsDrilldownModal with TekStak's render slots.
function TechStackDrilldown({ selectedVendor, departmentId, period, onClose }) {
  const componentId = selectedVendor?.vendorId;
  const componentName = selectedVendor?.productName;
  const vendorName = selectedVendor?.vendorName;

  const { startDate, endDate } = useMemo(() => {
    if (!period || period === 'all-time') return { startDate: null, endDate: null };
    return getDateRangeStrings(period);
  }, [period]);

  const params = useMemo(() => {
    const p = {
      nodeType: 'component_id',
      nodeValue: componentId,
      limit: 200,
      offset: 0,
    };
    if (departmentId) p.department = departmentId;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    return p;
  }, [componentId, departmentId, startDate, endDate]);

  const queryFn = useCallback(
    () => apiClient.get('/api/sankey/drilldown', { params }),
    [params]
  );

  // Component metadata for the meta-block (description + capability).
  const { data: catalog } = useQuery({
    queryKey: ['tech-components', 'catalog'],
    queryFn: () => apiClient.get('/api/tech-components'),
    staleTime: 1000 * 60 * 60,
  });
  const componentMeta = useMemo(() => {
    if (!catalog || !componentId) return null;
    const c = catalog.find(x => x.component_id === Number(componentId));
    if (!c) return null;
    return {
      description: c.description || null,
      capability: c.business_function || null,
    };
  }, [catalog, componentId]);

  const fmtDate = (d) => (d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—');

  const chips = [
    departmentId && { icon: <Building2 size={12} />, label: `Dept ${departmentId}` },
    (startDate || endDate)
      ? { icon: <Calendar size={12} />, label: `${fmtDate(startDate)} – ${fmtDate(endDate)}` }
      : { icon: <Calendar size={12} />, label: 'All time' },
    componentId && { icon: <Hash size={12} />, label: `id #${componentId}` },
  ].filter(Boolean);

  const handleFlag = useCallback(async (row, currentlyFlagged) => {
    logger.ui.info('TechStack', 'drilldown_flag', {
      id: row.id, was_flagged: currentlyFlagged, component_id: componentId,
    });
    // Use the classification_flags pipeline so flagged rows feed
    // classificationActiveLearning.getCandidates() and bubble to the
    // top of the AI re-review queue.
    if (currentlyFlagged) {
      await apiClient.post(`/api/classification/flags/item/${row.id}/resolve`, {
        resolution_type: 'dismissed_not_actionable',
        resolution_notes: `Unflagged from Tech Stack drilldown (component_id=${componentId})`,
        status: 'dismissed',
      });
    } else {
      await apiClient.post('/api/classification/flags', {
        item_id: row.id,
        flag_reason: `Flagged from Tech Stack drilldown — component=${componentName || `id #${componentId}`}, vendor=${vendorName || 'unknown'}`,
        flag_category: 'other',
        flag_severity: 'warning',
      });
    }
  }, [componentId, componentName, vendorName]);

  return (
    <SharedTransactionsDrilldownModal
      isOpen={true}
      onClose={onClose}
      title={componentName || vendorName || 'Product'}
      contextLabel={vendorName && componentName && vendorName !== componentName ? vendorName : null}
      chips={chips}
      queryKey={['tech-stack', 'drilldown', componentId, departmentId, startDate, endDate]}
      queryFn={queryFn}
      metaBlock={componentMeta}
      onFlagForReview={handleFlag}
      vendor={vendorName}
      departmentId={departmentId}
      component={componentName}
      startDate={startDate}
      endDate={endDate}
      copyContext={{
        vendor: vendorName,
        component: componentName,
        department: departmentId ? `Dept ${departmentId}` : null,
        period: period === 'all-time' ? 'All time' : `${fmtDate(startDate)} – ${fmtDate(endDate)}`,
      }}
      footerNote={`Scoped by tech_component_id #${componentId || '—'}. Click a PO to open the full document; flag any misclassified rows for AI review.`}
    />
  );
}

export default TechStackAnalysis;
